import { useQuery } from "@tanstack/react-query";
import { CheckCircleIcon, PlusIcon } from "@heroicons/react/24/outline";
import backend from "../../services/backend";
import { Link } from "../../components/Link";
import { TableSkeleton } from "../../components/Skeleton";
import SortableTable from "../../components/SortableTable";
import { Badge } from "../../catalyst/badge";
import { Button } from "../../catalyst/button";
import type { AttentionDocumentItem, AttentionStage } from "@avisoma/shared";

const REFRESH_INTERVAL = 5000;

const PROBLEM_BADGE: Record<
  AttentionDocumentItem["problem"],
  { label: string; color: "red" | "amber" | "zinc" | "blue" }
> = {
  failed: { label: "Failed", color: "red" },
  invalid: { label: "Invalid file", color: "amber" },
  stuck: { label: "Stuck", color: "amber" },
  pending: { label: "Pending", color: "zinc" },
  processing: { label: "Processing", color: "blue" },
};

const STAGE_LABELS: Record<AttentionStage, string> = {
  pdfParser: "Parser",
  metadata: "Metadata",
  embeddings: "Embeddings",
  classification: "Classification",
  dataExtraction: "Data Extraction",
  reconstruction: "Reconstruction",
};

function formatAge(since: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 60000));
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export default function NeedsAttentionTable() {
  const { data, isLoading: loading, error: fetchError, dataUpdatedAt } = useQuery({
    queryKey: ["attentionDocuments"],
    queryFn: () => backend.getDocumentAttention().then((res) => res.data),
    refetchInterval: REFRESH_INTERVAL,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const error = fetchError?.message ?? null;
  const lastUpdated = new Date(dataUpdatedAt);
  const countLabel =
    items.length === 0
      ? ""
      : totalCount > items.length
        ? ` (${items.length} of ${totalCount})`
        : ` (${items.length})`;

  return (
    <div>
      <div className="py-5 border-b border-gray-200 dark:border-zinc-700">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">
              Needs Attention{countLabel}
            </h2>
            <span className="text-sm text-gray-500 dark:text-zinc-400">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          </div>
          <Button color="indigo" href="/documents/upload">
            <PlusIcon className="size-5" />
            Upload New
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="px-4 py-5 sm:p-6">
          <TableSkeleton />
        </div>
      ) : error ? (
        <div className="px-4 sm:p-6 text-center py-12 text-red-600 dark:text-red-400">{error}</div>
      ) : items.length === 0 ? (
        <div className="px-4 sm:p-6 text-center py-12 text-gray-500 dark:text-zinc-400">
          <CheckCircleIcon className="mx-auto size-8 text-green-500 dark:text-green-400" />
          <p className="mt-2">All documents healthy — nothing needs attention.</p>
        </div>
      ) : (
        <SortableTable
          storageId="needs-attention"
          columns={[
            {
              accessor: "fileName",
              label: "File Name",
              render: (doc) => <Link to={`/documents/${doc.id}`}>{doc.fileName}</Link>,
            },
            {
              accessor: "problem",
              label: "Problem",
              render: (doc) => (
                <Badge color={PROBLEM_BADGE[doc.problem].color}>{PROBLEM_BADGE[doc.problem].label}</Badge>
              ),
            },
            {
              accessor: "stage",
              label: "Stage",
              render: (doc) => (doc.stage ? STAGE_LABELS[doc.stage] : "—"),
            },
            {
              accessor: "errorPages",
              label: "Pages",
              render: (doc) => (doc.errorPages > 0 ? `${doc.errorPages}/${doc.totalPages}` : "—"),
            },
            {
              accessor: "since",
              label: "Age",
              render: (doc) => formatAge(doc.since),
            },
          ]}
          data={items}
          rowKey={(row) => row.id}
        />
      )}

      {totalCount > items.length ? (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
          <Link to="/documents?attention=1" className="text-sm font-medium">
            View all {totalCount} needing attention →
          </Link>
        </div>
      ) : null}
    </div>
  );
}
